import { z } from 'zod';
import {
  STATUS_BY_ID,
  type Agenda,
  type AgendaItem,
  type CoverageCode,
  type DeadlineState,
  type MaintenanceDetail,
  type MaintenanceItem,
  type MaintenancePhoto,
  type MaintenanceStatus,
  type TechnicianFinancialItem,
  type TechnicianFinancialState,
  type TechnicianChargeComposition,
  type TechnicianFinancialSummary,
  type TechAppSummary,
  type TechPendency,
  type UrgencyLevel,
} from '@/domain/technician';

/**
 * Schemas Zod da fronteira C4 — tolerantes como a borda exige (WhenWritingNull: campos ausentes,
 * nunca `null` explícito do backend; o que a tech-api devolve nulo de propósito chega como
 * `.nullish()`). A transformação entrega o modelo de domínio estrito (C3).
 */

const URGENCY_VALUES = ['URGENTE', 'AGENDADA'] as const;
const COVERAGE_VALUES = ['COBERTO', 'EXCEDENTE'] as const;
const AGENDA_KIND_VALUES = ['VISTORIA', 'CHECKUP', 'EMPRESTIMO'] as const;
const DEADLINE_VALUES = ['HOJE', 'NO_PRAZO', 'ATRASADO', 'SEM_PRAZO'] as const;
const PENDENCY_VALUES = ['RECEBIMENTO', 'CERTIFICACAO_VENCIDA', 'CERTIFICACAO_A_VENCER'] as const;
const FINANCIAL_STATE_VALUES = ['EXPECTED', 'PENDING', 'COMMISSION_PENDING', 'RECEIVED', 'RECEIVED_DIRECT', 'REFUNDED', 'DISPUTED'] as const;
const FINANCIAL_SOURCE_VALUES = ['MAINTENANCE_PAYMENT', 'EXTERNAL_CASH', 'LEGACY_SNAPSHOT'] as const;

const financialNumber = z.coerce.number().finite();

const STATUS_ID_SCHEMA = z.union([
  z.literal(1), z.literal(5), z.literal(6), z.literal(7),
  z.literal(2), z.literal(8), z.literal(3), z.literal(4),
]);

function toStatus(statusId: number): MaintenanceStatus {
  const status = STATUS_BY_ID[statusId];
  if (status === undefined) throw new Error(`Status de chamado desconhecido: ${String(statusId)}`);
  return status;
}

const MaintenanceItemSchema = z
  .object({
    id: z.string().min(1),
    equipmentName: z.string().min(1),
    problemDescription: z.string(),
    requesterName: z.string().nullish(),
    statusId: STATUS_ID_SCHEMA,
    urgencyLevel: z.enum(URGENCY_VALUES).nullish(),
    companyName: z.string().nullish(),
    companyCity: z.string().nullish(),
    companyState: z.string().nullish(),
    distanceKm: z.number().nullish(),
    grossAmount: z.number().nullish(),
    platformFeeAmount: z.number().nullish(),
    technicianReceivableAmount: z.number().nullish(),
    netAmount: z.number().nullish(),
    planCode: z.string().nullish(),
    coverageCode: z.enum(COVERAGE_VALUES).nullish(),
    exclusiveUntil: z.string().nullish(),
    isPreferredOffer: z.boolean().optional(),
  })
  .transform((raw): MaintenanceItem => ({
    id: raw.id,
    equipmentName: raw.equipmentName,
    problemDescription: raw.problemDescription,
    requesterName: raw.requesterName ?? '',
    status: toStatus(raw.statusId),
    urgency: (raw.urgencyLevel ?? null) satisfies UrgencyLevel | null,
    companyName: raw.companyName ?? null,
    companyCity: raw.companyCity ?? null,
    companyState: raw.companyState ?? null,
    distanceKm: raw.distanceKm ?? null,
    grossAmount: raw.grossAmount ?? null,
    platformFeeAmount: raw.platformFeeAmount ?? null,
    technicianReceivableAmount: raw.technicianReceivableAmount ?? null,
    netAmount: raw.netAmount ?? null,
    planCode: raw.planCode ?? null,
    coverage: (raw.coverageCode ?? null) satisfies CoverageCode | null,
    exclusiveUntil: raw.exclusiveUntil ?? null,
    isPreferredOffer: raw.isPreferredOffer ?? false,
  }));

const MaintenancePhotoSchema = z
  .object({
    id: z.string().min(1),
    photoType: z.enum([
      'ANTES',
      'DEPOIS',
      'COMPROVANTE_PIX',
      'COMPROVANTE_PAGAMENTO',
      'COMPROVANTE_REPASSE',
    ]),
    contentUrl: z.string().min(1),
  })
  .transform((raw): MaintenancePhoto => ({
    id: raw.id,
    photoType: raw.photoType,
    contentUrl: raw.contentUrl,
  }));

const FinancialItemSchema = z
  .object({
    maintenanceRequestId: z.string().min(1),
    source: z.enum(FINANCIAL_SOURCE_VALUES),
    paymentId: z.string().nullish(),
    paymentStatus: z.string().nullish(),
    payoutStatus: z.string().nullish(),
    payoutMethod: z.string().nullish(),
    grossAmount: financialNumber,
    platformCommissionAmount: financialNumber,
    gatewayFee: financialNumber.nullish(),
    gatewayFeeKnown: z.boolean(),
    expectedAmount: financialNumber,
    pendingAmount: financialNumber,
    receivedAmount: financialNumber,
    receivedDirectAmount: financialNumber,
    refundedAmount: financialNumber,
    disputedAmount: financialNumber,
    occurredAt: z.string().nullish(),
    payoutPaidAt: z.string().nullish(),
    reversedAt: z.string().nullish(),
    isComplete: z.boolean(),
    missingEvidence: z.array(z.string().min(1)).nullish(),
    disputedAt: z.string().nullish(),
  })
  // O demonstrativo nasce em rota/field aditivo; campos futuros não devem derrubar o detalhe
  // durante rollout. O domínio recebe somente as propriedades que conhece.
  .passthrough()
  .transform((raw): TechnicianFinancialItem => ({
    maintenanceRequestId: raw.maintenanceRequestId,
    source: raw.source,
    paymentId: raw.paymentId ?? null,
    paymentStatus: raw.paymentStatus ?? null,
    payoutStatus: raw.payoutStatus ?? null,
    payoutMethod: raw.payoutMethod ?? null,
    grossAmount: raw.grossAmount,
    platformCommissionAmount: raw.platformCommissionAmount,
    gatewayFee: raw.gatewayFee ?? null,
    gatewayFeeKnown: raw.gatewayFeeKnown,
    expectedAmount: raw.expectedAmount,
    pendingAmount: raw.pendingAmount,
    receivedAmount: raw.receivedAmount,
    receivedDirectAmount: raw.receivedDirectAmount,
    refundedAmount: raw.refundedAmount,
    disputedAmount: raw.disputedAmount,
    occurredAt: raw.occurredAt ?? null,
    payoutPaidAt: raw.payoutPaidAt ?? null,
    reversedAt: raw.reversedAt ?? null,
    isComplete: raw.isComplete,
    missingEvidence: raw.missingEvidence ?? [],
    disputedAt: raw.disputedAt ?? null,
  }));

/** Componentes persistidos da cobrança. Valores nulos ficam nulos: não reconstruímos histórico
 *  a partir de `finalValue`, que pode representar cobertura ou cobrança parcial. */
const ChargeCompositionSchema = z
  .object({
    serviceAmountDue: financialNumber.nullish(),
    urgencyAmountDue: financialNumber.nullish(),
    partsAmountDue: financialNumber.nullish(),
    platformFundedAmount: financialNumber.nullish(),
    clinicAmountDue: financialNumber.nullish(),
    isComplete: z.boolean().nullish(),
    missingComponents: z.array(z.string().min(1)).nullish(),
  })
  .passthrough()
  .transform((raw): TechnicianChargeComposition => ({
    serviceAmountDue: raw.serviceAmountDue ?? null,
    urgencyAmountDue: raw.urgencyAmountDue ?? null,
    partsAmountDue: raw.partsAmountDue ?? null,
    platformFundedAmount: raw.platformFundedAmount ?? null,
    clinicAmountDue: raw.clinicAmountDue ?? null,
    isComplete: raw.isComplete ?? null,
    missingComponents: raw.missingComponents ?? [],
  }));

const FinancialSummarySchema = z
  .object({
    expectedAmount: financialNumber,
    pendingAmount: financialNumber,
    receivedAmount: financialNumber,
    receivedDirectAmount: financialNumber,
    refundedAmount: financialNumber,
    disputedAmount: financialNumber,
    gatewayFee: financialNumber.nullish(),
    gatewayFeeKnown: z.boolean(),
    isComplete: z.boolean(),
    states: z.array(z.enum(FINANCIAL_STATE_VALUES)),
    items: z.array(FinancialItemSchema),
    chargeComposition: ChargeCompositionSchema.nullish(),
    incompletenessReasons: z.array(z.string().min(1)).nullish(),
  })
  .passthrough()
  .transform((raw): TechnicianFinancialSummary => ({
    expectedAmount: raw.expectedAmount,
    pendingAmount: raw.pendingAmount,
    receivedAmount: raw.receivedAmount,
    receivedDirectAmount: raw.receivedDirectAmount,
    refundedAmount: raw.refundedAmount,
    disputedAmount: raw.disputedAmount,
    gatewayFee: raw.gatewayFee ?? null,
    gatewayFeeKnown: raw.gatewayFeeKnown,
    isComplete: raw.isComplete,
    states: raw.states satisfies readonly TechnicianFinancialState[],
    items: raw.items,
    chargeComposition: raw.chargeComposition ?? null,
    incompletenessReasons: raw.incompletenessReasons ?? [],
  }));

const MaintenanceDetailSchema = z
  .object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    companyName: z.string().min(1),
    requesterName: z.string().nullish(),
    equipmentName: z.string().min(1),
    problemDescription: z.string(),
    statusId: STATUS_ID_SCHEMA,
    urgencyLevel: z.enum(URGENCY_VALUES).nullish(),
    scheduledAt: z.string().nullish(),
    createdAt: z.string(),
    requestNumber: z.string().nullish(),
    companyAddressLine1: z.string().nullish(),
    companyAddressLine2: z.string().nullish(),
    companyCity: z.string().nullish(),
    companyState: z.string().nullish(),
    companyLatitude: z.number().nullish(),
    companyLongitude: z.number().nullish(),
    distanceKm: z.number().nullish(),
    clientAverageRating: z.number().optional(),
    clientRatingsCount: z.number().optional(),
    grossAmount: z.number().nullish(),
    platformFeeAmount: z.number().nullish(),
    technicianReceivableAmount: z.number().nullish(),
    netAmount: z.number().nullish(),
    planCode: z.string().nullish(),
    coverageCode: z.enum(COVERAGE_VALUES).nullish(),
    exclusiveUntil: z.string().nullish(),
    photos: z.array(MaintenancePhotoSchema).optional(),
    equipmentSerialNumber: z.string().nullish(),
    paymentComment: z.string().nullish(),
    // ── Corte 2.1 — fluxo de atendimento (tolerantes: WhenWritingNull ⇒ ausentes) ──
    technicianCurrentScreenCode: z.string().nullish(),
    paymentMethodName: z.string().nullish(),
    commercialModelSnapshot: z.string().nullish(),
    openingFeeSnapshot: z.number().nullish(),
    commissionPercentage: z.number().nullish(),
    finalValue: z.number().nullish(),
    serviceValueOffered: z.number().nullish(),
    minimumServiceValue: z.number().nullish(),
    equipmentCategoryId: z.number().nullish(),
    equipmentCategoryName: z.string().nullish(),
    technicianEquipmentCategoryId: z.number().nullish(),
    technicianEquipmentCategoryName: z.string().nullish(),
    financialSummary: FinancialSummarySchema.nullish(),
  })
  .transform((raw): MaintenanceDetail => ({
    id: raw.id,
    companyId: raw.companyId,
    companyName: raw.companyName,
    requesterName: raw.requesterName ?? '',
    equipmentName: raw.equipmentName,
    problemDescription: raw.problemDescription,
    status: toStatus(raw.statusId),
    urgency: (raw.urgencyLevel ?? null) satisfies UrgencyLevel | null,
    scheduledAt: raw.scheduledAt ?? null,
    createdAt: raw.createdAt,
    requestNumber: raw.requestNumber ?? null,
    companyAddressLine1: raw.companyAddressLine1 ?? null,
    companyAddressLine2: raw.companyAddressLine2 ?? null,
    companyCity: raw.companyCity ?? null,
    companyState: raw.companyState ?? null,
    companyLatitude: raw.companyLatitude ?? null,
    companyLongitude: raw.companyLongitude ?? null,
    distanceKm: raw.distanceKm ?? null,
    clientAverageRating: raw.clientAverageRating ?? 0,
    clientRatingsCount: raw.clientRatingsCount ?? 0,
    grossAmount: raw.grossAmount ?? null,
    platformFeeAmount: raw.platformFeeAmount ?? null,
    technicianReceivableAmount: raw.technicianReceivableAmount ?? null,
    netAmount: raw.netAmount ?? null,
    planCode: raw.planCode ?? null,
    coverage: (raw.coverageCode ?? null) satisfies CoverageCode | null,
    exclusiveUntil: raw.exclusiveUntil ?? null,
    photos: raw.photos ?? [],
    equipmentSerialNumber: raw.equipmentSerialNumber ?? null,
    paymentComment: raw.paymentComment ?? null,
    technicianCurrentScreenCode: raw.technicianCurrentScreenCode ?? null,
    paymentMethodName: raw.paymentMethodName ?? null,
    commercialModelSnapshot: raw.commercialModelSnapshot ?? null,
    openingFeeSnapshot: raw.openingFeeSnapshot ?? null,
    commissionPercentage: raw.commissionPercentage ?? null,
    finalValue: raw.finalValue ?? null,
    serviceValueOffered: raw.serviceValueOffered ?? 0,
    minimumServiceValue: raw.minimumServiceValue ?? null,
    equipmentCategoryId: raw.equipmentCategoryId ?? null,
    equipmentCategoryName: raw.equipmentCategoryName ?? null,
    technicianEquipmentCategoryId: raw.technicianEquipmentCategoryId ?? null,
    technicianEquipmentCategoryName: raw.technicianEquipmentCategoryName ?? null,
    financialSummary: raw.financialSummary ?? null,
  }));

const AgendaItemSchema = z
  .object({
    kind: z.enum(AGENDA_KIND_VALUES),
    id: z.string().min(1),
    title: z.string().min(1),
    companyName: z.string().nullish(),
    scheduledAt: z.string().nullish(),
    dueAt: z.string().nullish(),
    deadlineState: z.enum(DEADLINE_VALUES),
    equipmentCount: z.number().nullish(),
    route: z.string().min(1),
  })
  // O `route` do backend é informação, nunca a fonte da navegação (a tela monta a rota a partir
  // do kind+id tipados); aqui só se garante que o contrato não mudou o prefixo canônico.
  .superRefine((raw, ctx) => {
    const expected = raw.kind === 'VISTORIA' ? '/auditoria/' : raw.kind === 'CHECKUP' ? '/checkup/' : '/emprestimo/';
    if (!raw.route.startsWith(expected)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['route'], message: `Rota da agenda não bate com o tipo ${raw.kind}` });
    }
  })
  .transform((raw): AgendaItem => ({
    kind: raw.kind,
    id: raw.id,
    title: raw.title,
    companyName: raw.companyName ?? null,
    scheduledAt: raw.scheduledAt ?? null,
    dueAt: raw.dueAt ?? null,
    deadlineState: raw.deadlineState satisfies DeadlineState,
    equipmentCount: raw.equipmentCount ?? null,
    route: raw.route,
  }));

const AgendaSchema = z
  .object({
    items: z.array(AgendaItemSchema),
    totalCount: z.number(),
    overdueCount: z.number(),
    todayCount: z.number(),
  })
  .transform((raw): Agenda => ({
    items: raw.items,
    totalCount: raw.totalCount,
    overdueCount: raw.overdueCount,
    todayCount: raw.todayCount,
  }));

const PendencySchema = z
  .object({
    code: z.enum(PENDENCY_VALUES),
    message: z.string().min(1),
    route: z.string().min(1),
  })
  .transform((raw): TechPendency => ({
    code: raw.code,
    message: raw.message,
    route: raw.route,
  }));

const AppSummarySchema = z
  .object({
    isAvailable: z.boolean(),
    monthlyNetEarnings: z.number(),
    monthlyServiceCount: z.number(),
    averageRating: z.number(),
    resumableService: MaintenanceItemSchema.nullish(),
    activeServiceCount: z.number(),
    agendaPreview: z.array(AgendaItemSchema).optional(),
    agendaTodayCount: z.number(),
    agendaOverdueCount: z.number(),
    nearbyOffers: z.array(MaintenanceItemSchema).optional(),
    availableOfferCount: z.number(),
    pendencies: z.array(PendencySchema).optional(),
    nextPayoutAmount: z.number().nullish(),
    nextPayoutDate: z.string().nullish(),
  })
  .transform((raw): TechAppSummary => ({
    isAvailable: raw.isAvailable,
    monthlyNetEarnings: raw.monthlyNetEarnings,
    monthlyServiceCount: raw.monthlyServiceCount,
    averageRating: raw.averageRating,
    resumableService: raw.resumableService ?? null,
    activeServiceCount: raw.activeServiceCount,
    agendaPreview: raw.agendaPreview ?? [],
    agendaTodayCount: raw.agendaTodayCount,
    agendaOverdueCount: raw.agendaOverdueCount,
    nearbyOffers: raw.nearbyOffers ?? [],
    availableOfferCount: raw.availableOfferCount,
    pendencies: raw.pendencies ?? [],
    nextPayoutAmount: raw.nextPayoutAmount ?? null,
    nextPayoutDate: raw.nextPayoutDate ?? null,
  }));

const MaintenanceListResponseSchema = z.object({
  items: z.array(MaintenanceItemSchema),
  totalCount: z.number(),
  page: z.number(),
  size: z.number(),
});

export function parseMaintenanceItem(payload: unknown): MaintenanceItem {
  return MaintenanceItemSchema.parse(payload);
}

export function parseMaintenanceDetail(payload: unknown): MaintenanceDetail {
  return MaintenanceDetailSchema.parse(payload);
}

export function parseMaintenanceList(payload: unknown): { items: MaintenanceItem[]; totalCount: number } {
  const parsed = MaintenanceListResponseSchema.parse(payload);
  return { items: parsed.items, totalCount: parsed.totalCount };
}

export function parseAgenda(payload: unknown): Agenda {
  return AgendaSchema.parse(payload);
}

export function parseAppSummary(payload: unknown): TechAppSummary {
  return AppSummarySchema.parse(payload);
}
